import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useDispatch, useSelector } from 'hooks';
import { GeneralInput } from 'components/input';
// import { Log, RemarkLog } from 'components/log';
import Button from '@mui/material/Button';
import get from 'lodash/get';
import * as Models from 'models';
import { uncapitalize } from 'lib/utils';
import * as Actions from '@/redux/actions';

function DialogView({ open, onClose, model, id, payload = {} }) {
  const { itemInfo, optionsItemInfo } = useSelector((state) => ({
    itemInfo: get(state, `${uncapitalize(model)}Info`),
    optionsItemInfo: get(state, `options${model}Info`),
    // remarkLogsInfo: state.remarkLogsInfo,
  }), shallowEqual);
  const dispatch = useDispatch();
  const _optionFilters = Models[model].optionFilters || [];
  const optionFilters = useMemo(() => {
    return JSON.stringify(_optionFilters.reduce((acc, value, i) => {
      acc[Models[model].optionFilters[i]] = value;
      return acc;
    }, {}));
  }, [_optionFilters]);
  const modelName = useMemo(() => model.replace(/([A-Z])/g, ' $1').trim(), [model]);
  const fields = useMemo(() => {
    const ret = [];

    for (const row of Models[model].form.edit) {
      // if row is string
      if (typeof row === 'string') {
        ret.push(Models[model].fields[row]);
      } else {
        const tmp = [];
        for (const field of row) {
          tmp.push(Models[model].fields[field]);
        }
        ret.push(tmp);
      }
    }

    return ret;
  }, [model]);
  const isFetching = itemInfo.isFetching;

  useEffect(() => {
    if (id) {
      dispatch(Actions[`read${model}`]({ payload: { ...payload, id } }));

      /*
      dispatch(Actions.readRemarkLogs({
        payload: {
          filters: {
            objectId: id,
          },
          sort: 'createdAt',
          order: 'desc',
        }
      }));
      */
    }
  }, [dispatch, id, model]);
  useEffect(() => {
    if (Actions[`readOptions${model}`]) {
      dispatch(Actions[`readOptions${model}`]());
    }
  }, [dispatch, model]);
  useEffect(() => {
    if (Actions[`readOptions${model}`]) {
      dispatch(Actions[`readOptions${model}`]({
        payload: JSON.parse(optionFilters),
      }));
    }
  }, [optionFilters]);

  return (
    <Dialog
      fullWidth
      maxWidth='xl'
      open={open}
      onClose={isFetching ? undefined : onClose}
    >
      <DialogTitle>View {modelName}</DialogTitle>
      <DialogContent>
        <Grid container direction='column' gap={3}>
          <Grid container spacing={2}>
            {
              fields.map((row, i) => {
                const render = (field) => {
                  if (field?.type === 'table') {
                    return (
                      <Table>
                        <TableHead>
                          <TableRow>
                            {
                              field.columns.map((f) => f.type !== 'hidden' && (
                                <TableCell key={`form-${model}-view-table-head-${f.name}-cell`}>
                                  {f.label}
                                </TableCell>
                              ))
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            itemInfo.data[field.name]?.map((item, j) => (
                              <TableRow key={`form-${model}-view-table-row-${j}`}>
                                {
                                  field.columns.map((c) => c.type !== 'hidden' && (
                                    <TableCell key={`form-${model}-view-table-cell-${c.name}`}>
                                      <GeneralInput
                                        {...c}
                                        key={`form-${model}-view-table-cell-${c.name}`}
                                        name={`${field.name}[${j}].${c.name}`}
                                        type={c.type}
                                        label={null}
                                        value={get(item, c.name)}
                                        options={c.options || get(optionsItemInfo, `data.${c.name}`)}
                                        readOnly
                                      />
                                    </TableCell>
                                  ))
                                }
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    );
                  } else {
                    const { name, type, options, ...fieldProps } = field;
                    return (
                      <GeneralInput
                        key={`form-${model}-view-input-${i}-${name}-input`}
                        name={name}
                        type={type}
                        value={itemInfo.data ? itemInfo.data[name] : ''}
                        options={options || get(optionsItemInfo, `data.${name}`)}
                        readOnly
                        {...fieldProps}
                      />
                    );
                  }
                };

                if (!Array.isArray(row)) {
                  return render(row);
                } else {
                  const ret = [];
                  for (const field of row) {
                    ret.push(
                      <Grid
                        key={`form-${model}-view-row-${i}-${field.name}`}
                        size={{ xs: 12, md: 12 / row.length }}
                      >
                        {render(field)}
                      </Grid>
                    );
                  }
                  return ret;
                }
              })
            }
          </Grid>

          {/* <Grid>
            <SectionWrapper title='Remarks'>
              <RemarkLog info={remarkLogsInfo} />
            </SectionWrapper>
          </Grid>

          <Grid>
            <SectionWrapper title='Activities'>
              <Log id={id} />
            </SectionWrapper>
          </Grid> */}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={isFetching}>
          Close
        </Button>
      </DialogActions>
      {isFetching && <LinearProgress />}
    </Dialog>
  );
}

DialogView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  model: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  payload: PropTypes.object,
};

export default DialogView;
